import React, { FunctionComponent } from "react";

import { useNullableDealerAccountInfo } from "../../../../../stores/hooks/useNullableDealerAccountInfo";
import { isMbApproved } from "../../../../../util/isMbApproved";
import { isStarApproved } from "../../../../../util/isStarApproved";
import { ModifiedTirePartItem } from "../../useTireResults";
import { Best } from "./Best";
import { Better } from "./Better";
import { Good } from "./Good";
import { MbApproved } from "./MbApproved";
import { OETire } from "./OETire";
import { StarApproved } from "./StarApproved";

type RibbonProps = {
  className?: string;
  index: number;
  part: ModifiedTirePartItem | null;
};

/**
 * The API's featured position is the dealer's configured slot: 1 = Good,
 * 2 = Better, 3 = Best. A slot can be empty (for example no "Good" brand in
 * stock), so the ribbon must come from the position, not from where the tile
 * lands in the list (TW-1080).
 */
const ribbonByPosition: Record<
  number,
  FunctionComponent<{ className?: string }>
> = {
  1: Good,
  2: Better,
  3: Best,
};

export const useRibbon = ({
  className,
  index,
  part,
}: RibbonProps): JSX.Element | null => {
  const dealer = useNullableDealerAccountInfo();

  const isOriginalEquipment =
    part?.partDetails.primaryPart.tireOriginalEquipmentFlag ?? false;

  if (
    isOriginalEquipment &&
    isMbApproved(dealer?.dealerNationalAccountId ?? "")
  ) {
    return <MbApproved className={className} />;
  }

  if (
    isOriginalEquipment &&
    isStarApproved(dealer?.dealerNationalAccountId ?? "")
  ) {
    return <StarApproved className={className} />;
  }

  if (part?.featured) {
    // List order is only a fallback for a response without a position.
    const position = part.featuredPosition ?? index + 1;
    const Component = ribbonByPosition[position] ?? Good;
    return <Component className={className} />;
  }

  if (isOriginalEquipment) {
    return <OETire className={className} />;
  }

  return null;
};
